import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { ShieldAlert, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { openManualInvestigation, getIncidentReports } from "../../lib/api";

const HHB_TYPES = ["Bullying", "Harassment", "Hazing", "Bullying and/or Harassment"];

const sName = (s) => `${s.preferred_name || s.first_name || ""} ${s.last_name || ""}`.trim() || "Unknown";

function StudentPicker({ label, color, students, selected, toggle, filter, testid }) {
  const list = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return students.filter((s) => !f || sName(s).toLowerCase().includes(f));
  }, [students, filter]);
  const on = color === "red"
    ? "bg-red-100 border-red-300 text-red-800"
    : "bg-orange-100 border-orange-300 text-orange-800";
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1 border rounded" data-testid={testid}>
        {list.length === 0 && <span className="text-xs text-muted-foreground p-1">No matching students</span>}
        {list.map((s) => (
          <label key={s.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs cursor-pointer border transition-colors ${selected.includes(s.id) ? on : "bg-white border-gray-200 hover:border-gray-300"}`}>
            <input type="checkbox" className="sr-only" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
            {sName(s)}
          </label>
        ))}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selected.map((id) => sName(students.find((s) => s.id === id) || {})).join(", ")}
        </p>
      )}
    </div>
  );
}

export default function OpenInvestigationDialog({ open, onClose, students = [], prefill = {}, onOpened }) {
  const [victimIds, setVictimIds] = useState([]);
  const [aggressorIds, setAggressorIds] = useState([]);
  const [incidentType, setIncidentType] = useState("Bullying and/or Harassment");
  const [descriptor, setDescriptor] = useState("");
  const [reason, setReason] = useState("");
  const [linkedIds, setLinkedIds] = useState([]);
  const [filter, setFilter] = useState("");
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dup, setDup] = useState(null);

  const nameMap = useMemo(() => {
    const m = {};
    students.forEach((s) => { if (s.id) m[s.id] = sName(s); });
    return m;
  }, [students]);

  useEffect(() => {
    if (!open) return;
    setVictimIds(prefill.victimIds || []);
    setAggressorIds(prefill.aggressorIds || []);
    setIncidentType("Bullying and/or Harassment");
    setDescriptor("");
    setReason("");
    setLinkedIds(prefill.linkedReportIds || []);
    setFilter("");
    setShowReports(false);
    setDup(null);
    (async () => {
      try {
        const res = await getIncidentReports({ limit: 1000 });
        setReports((res.data || []).filter((r) => (r.type || "").toLowerCase() === "incident"));
      } catch { /* non-blocking */ }
    })();
  }, [open, prefill.victimIds, prefill.aggressorIds, prefill.linkedReportIds]);

  const toggle = (list, setList) => (id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const submit = useCallback(async (confirm) => {
    if (!reason.trim()) { toast.error("A reason for opening is required"); return; }
    if (victimIds.length === 0 || aggressorIds.length === 0) {
      toast.error("Select at least one alleged victim and one alleged perpetrator"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await openManualInvestigation({
        victim_ids: victimIds, aggressor_ids: aggressorIds,
        reason: reason.trim(), incident_type: incidentType,
        incident_descriptor: descriptor.trim(), linked_report_ids: linkedIds, confirm,
      });
      if (data.needs_confirm) { setDup(data); setSubmitting(false); return; }
      const created = data.created || [];
      const linked = data.linked || [];
      if (created.length) toast.success(`Investigation opened — ${created[0].victim_name} / ${created[0].aggressor_name}`);
      else if (linked.length) toast.success("Incident linked to the existing investigation");
      onClose();
      if (onOpened) onOpened((created[0] && created[0].id) || linked[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open investigation");
    }
    setSubmitting(false);
  }, [reason, victimIds, aggressorIds, incidentType, descriptor, linkedIds, onClose, onOpened]);

  const otherReports = reports.filter((r) => !(prefill.linkedReportIds || []).includes(r.id));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="open-investigation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#00305E]" /> Open HHB Investigation</DialogTitle>
          <DialogDescription>
            {prefill.contextLabel
              ? prefill.contextLabel
              : "Open a bullying / harassment / hazing investigation for a complaint that doesn't have an incident report yet."}
          </DialogDescription>
        </DialogHeader>

        {dup ? (
          <div className="space-y-3" data-testid="open-investigation-dup">
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <p className="font-medium text-amber-800">An open investigation already exists for:</p>
                <ul className="mt-1 list-disc ml-5 text-amber-900">
                  {dup.duplicates.map((d) => (
                    <li key={d.id}>{d.victim_name} (target) / {d.aggressor_name} (perpetrator) — opened {d.date_opened}</li>
                  ))}
                </ul>
                <p className="mt-2 text-amber-800">You can link this complaint/incident to the existing investigation instead of creating a duplicate{dup.new_pairs ? `, and still open ${dup.new_pairs} new one(s) for the remaining pair(s)` : ""}.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDup(null)} disabled={submitting} data-testid="open-investigation-dup-back">Go back</Button>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => submit(true)} disabled={submitting} data-testid="open-investigation-dup-link">
                {submitting ? "Working…" : "Link to existing"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <Input placeholder="Search students…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="open-investigation-search" />
            <StudentPicker label="Alleged victim(s)" color="red" students={students} selected={victimIds} toggle={toggle(victimIds, setVictimIds)} filter={filter} testid="open-investigation-victims" />
            <StudentPicker label="Alleged perpetrator(s)" color="orange" students={students} selected={aggressorIds} toggle={toggle(aggressorIds, setAggressorIds)} filter={filter} testid="open-investigation-aggressors" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Incident type</Label>
                <Select value={incidentType} onValueChange={setIncidentType}>
                  <SelectTrigger data-testid="open-investigation-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{HHB_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Descriptor (optional)</Label>
                <Input placeholder="e.g. sexual harassment" value={descriptor} onChange={(e) => setDescriptor(e.target.value)} data-testid="open-investigation-descriptor" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Reason for opening <span className="text-red-600">*</span></Label>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Formal complaint from parent · Single incident meets harassment definition"
                className="w-full text-sm border rounded px-2 py-1.5" data-testid="open-investigation-reason" />
            </div>

            <div className="space-y-1.5">
              <button type="button" onClick={() => setShowReports((v) => !v)} className="flex items-center gap-1 text-sm font-medium text-[#00305E]" data-testid="open-investigation-link-toggle">
                {showReports ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Link additional incident reports ({linkedIds.filter((id) => !(prefill.linkedReportIds || []).includes(id)).length} selected)
              </button>
              {showReports && (
                <div className="max-h-40 overflow-y-auto border rounded p-1 space-y-1" data-testid="open-investigation-reports">
                  {otherReports.length === 0 && <p className="text-xs text-muted-foreground p-1">No other incident reports.</p>}
                  {otherReports.map((r) => (
                    <label key={r.id} className="flex items-start gap-2 text-xs p-1.5 rounded hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={linkedIds.includes(r.id)} onChange={() => setLinkedIds((p) => p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id])} className="mt-0.5" />
                      <span><span className="font-medium">{r.date}</span> · {nameMap[r.student_id] || "Unknown"}{r.incident_type ? ` · ${r.incident_type}` : ""} — {(r.description || "").slice(0, 80)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => submit(false)} disabled={submitting} data-testid="open-investigation-submit">
                {submitting ? "Opening…" : "Open Investigation"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
